import { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, Link2, MapPin } from 'lucide-react';

import { useTenant } from '@/contexts/TenantContext';
import { MapCanvas } from '@/components/maps/MapCanvas';
import { MapLegend } from '@/components/maps/MapLegend';
import { LongPressPin } from '@/components/maps/LongPressPin';
import { PinPropertyAction } from '@/components/maps/PinPropertyAction';
import {
  ParcelDetailDrawer,
  type ParcelDrawerMember,
} from '@/components/maps/ParcelDetailDrawer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  PARCEL_LEGEND_ITEMS,
  getParcelRef,
  formatParcelLabel,
  type ParcelRecord,
} from '@/utils/parcelGeometry';
// Legacy JS API modules + MUI dialog — no TS types, imported as `any`.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import MemberLinkDialogRaw from '@/components/fpa/MemberLinkDialog';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { listLinkedParcelRefs } from '@/api/parcels';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
import { getMemberCached } from '@/api/fpaMembers';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const MemberLinkDialog = MemberLinkDialogRaw as any;

type Mode = 'view' | 'link';

interface LinkTarget {
  parcelRef: string;
  parcelLabel: string;
}

/**
 * Members-page "Map" tab: a mobile-friendly parcel map with two modes.
 *
 * - **View** (default): tapping a parcel opens a read-only detail drawer.
 * - **Link**: tapping a parcel opens the same drawer with a "Manage members"
 *   action that launches the link/unlink dialog.
 *
 * This is a self-contained clone of the admin `TenantParcelsMap` linking flow
 * (member enrichment → hazard-coloured labels → `ParcelDetailDrawer` →
 * `MemberLinkDialog`), scoped to the FPA selected in the top bar.
 */
export default function MembersParcelMap() {
  const { tenantId } = useTenant();

  const [mode, setMode] = useState<Mode>('view');

  // parcel_ref → linked member ids / hazards / member summaries.
  const [linkedRefs, setLinkedRefs] = useState<Set<string>>(() => new Set());
  const [refToMemberIds, setRefToMemberIds] = useState<Map<string, number[]>>(
    () => new Map(),
  );
  const [refToHazards, setRefToHazards] = useState<Map<string, string[]>>(
    () => new Map(),
  );
  const [refToMembers, setRefToMembers] = useState<
    Map<string, ParcelDrawerMember[]>
  >(() => new Map());

  // Bumped after a link/unlink so the link set + overlay colouring refetch.
  const [refreshKey, setRefreshKey] = useState(0);
  const [parcelCount, setParcelCount] = useState<number | null>(null);

  const [selectedParcel, setSelectedParcel] = useState<ParcelRecord | null>(null);
  const [linkTarget, setLinkTarget] = useState<LinkTarget | null>(null);

  // ── Link set + member ids (refetched on refreshKey) ─────────────────────── //
  useEffect(() => {
    if (tenantId == null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await listLinkedParcelRefs(tenantId);
        if (cancelled) return;
        setLinkedRefs(new Set<string>(data?.linked_refs || []));
        const map = new Map<string, number[]>();
        for (const link of data?.links || []) {
          const arr = map.get(link.parcel_ref) || [];
          arr.push(link.member_id);
          map.set(link.parcel_ref, arr);
        }
        setRefToMemberIds(map);
      } catch (err) {
        if (!cancelled) console.warn('Failed to load linked refs:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, refreshKey]);

  // ── Resolve linked members (cache-first) → hazards + summaries per parcel ── //
  useEffect(() => {
    if (refToMemberIds.size === 0) {
      setRefToHazards(new Map());
      setRefToMembers(new Map());
      return undefined;
    }
    let cancelled = false;
    const uniqueIds = Array.from(
      new Set(Array.from(refToMemberIds.values()).flat()),
    );
    (async () => {
      const results = await Promise.allSettled(
        uniqueIds.map((mid) => getMemberCached(mid)),
      );
      if (cancelled) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const memberById = new Map<number, any>();
      results.forEach((r, i) => {
        const mid = uniqueIds[i];
        if (mid != null && r.status === 'fulfilled' && r.value) {
          memberById.set(mid, r.value);
        }
      });

      const hazardsByRef = new Map<string, string[]>();
      const membersByRef = new Map<string, ParcelDrawerMember[]>();
      for (const [ref, ids] of refToMemberIds.entries()) {
        const hazardSet = new Set<string>();
        const summaries: ParcelDrawerMember[] = [];
        for (const mid of ids) {
          const m = memberById.get(mid);
          for (const t of m?.hazard_tags || m?.hazards || []) hazardSet.add(t);
          summaries.push({
            id: mid,
            name: m?.name ?? null,
            ref_number: m?.ref_number ?? null,
            contacts: m?.contacts ?? [],
          });
        }
        if (hazardSet.size > 0) hazardsByRef.set(ref, Array.from(hazardSet));
        membersByRef.set(ref, summaries);
      }
      setRefToHazards(hazardsByRef);
      setRefToMembers(membersByRef);
    })();
    return () => {
      cancelled = true;
    };
  }, [refToMemberIds]);

  const handleParcelSelect = useCallback((parcel: ParcelRecord) => {
    setSelectedParcel(parcel);
  }, []);

  const handleManageMembers = useCallback(() => {
    if (!selectedParcel) return;
    setLinkTarget({
      parcelRef: getParcelRef(selectedParcel) || '',
      parcelLabel:
        formatParcelLabel(selectedParcel) || getParcelRef(selectedParcel) || '',
    });
    setSelectedParcel(null);
  }, [selectedParcel]);

  const handleLinkChanged = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  const selectedRef = selectedParcel ? getParcelRef(selectedParcel) : null;
  const selectedMembers = (selectedRef && refToMembers.get(selectedRef)) || [];
  const selectedHazards = (selectedRef && refToHazards.get(selectedRef)) || [];

  if (tenantId == null) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        Select an FPA in the top bar to view its parcels map.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Mode toggle — lives outside the map so tapping a parcel never
          accidentally toggles modes. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-md border border-border p-0.5">
          <Button
            type="button"
            size="sm"
            variant={mode === 'view' ? 'default' : 'ghost'}
            className="gap-2"
            onClick={() => setMode('view')}
          >
            <Eye className="h-4 w-4" />
            View
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === 'link' ? 'default' : 'ghost'}
            className="gap-2"
            onClick={() => setMode('link')}
          >
            <Link2 className="h-4 w-4" />
            Link members
          </Button>
        </div>

        <Badge variant="outline" className="gap-1">
          <MapPin className="h-3 w-3" />
          {linkedRefs.size} linked
          {parcelCount != null && (
            <>
              {` · ${(parcelCount - linkedRefs.size).toLocaleString()} unlinked`}
              {` · ${parcelCount.toLocaleString()} total`}
            </>
          )}
        </Badge>
      </div>

      {mode === 'link' && (
        <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm text-primary">
          Linking mode — tap any parcel to assign or remove FPA members. See the
          legend for what each shade means.
        </div>
      )}

      <MapCanvas
        center={[-25.6429, 28.3433]}
        zoom={11}
        variant="lg"
        gearMode="full"
        tenantId={tenantId}
        forceParcels
        forceBoundaries
        mapSearch
        parcelHazardByRef={refToHazards}
        parcelRefreshKey={refreshKey}
        legend={<MapLegend items={[...PARCEL_LEGEND_ITEMS]} />}
        onParcelSelect={handleParcelSelect}
        onParcelsLoaded={setParcelCount}
        className="!h-[70vh] !min-h-[420px]"
      >
        {/* Long-press anywhere to share that spot, and the property on it.
            `enabled` is unconditional because this page sets `forceParcels`:
            the parcels layer is always loaded here regardless of the gear
            toggle other maps read, so gating on that toggle would hide the
            action on the one map that is entirely about parcels. */}
        <LongPressPin actions={(pin) => <PinPropertyAction pin={pin} enabled />} />
      </MapCanvas>

      <ParcelDetailDrawer
        open={Boolean(selectedParcel)}
        onOpenChange={(o) => {
          if (!o) setSelectedParcel(null);
        }}
        parcel={selectedParcel}
        members={selectedMembers}
        hazards={selectedHazards}
        // Read-only in View mode (no button); editable in Link mode.
        onManageMembers={mode === 'link' ? handleManageMembers : undefined}
      />

      <MemberLinkDialog
        open={Boolean(linkTarget)}
        onClose={() => setLinkTarget(null)}
        parcelRef={linkTarget?.parcelRef}
        parcelLabel={linkTarget?.parcelLabel}
        initialSearch={linkTarget?.parcelLabel}
        tenantId={tenantId}
        onChange={handleLinkChanged}
      />
    </div>
  );
}
